import React, { useEffect, useMemo, useState } from "react";
import {
  Repeat, Plus, Trash2, Edit3, X, Play, PauseCircle, CalendarClock, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

const FREQUENCIES = [
  { key: "weekly",    label: "Weekly" },
  { key: "monthly",   label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "yearly",    label: "Yearly" },
];

const VOUCHER_TYPES = [
  { key: "sales",     label: "Sales" },
  { key: "purchase",  label: "Purchase" },
  { key: "payment",   label: "Payment" },
  { key: "receipt",   label: "Receipt" },
  { key: "journal",   label: "Journal" },
  { key: "contra",    label: "Contra" },
];

function emptyForm() {
  return {
    name: "", voucher_type: "journal",
    frequency: "monthly",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    narration: "", party_id: "",
    lines: [
      { ledger_id: "", dr: 0, cr: 0, narration: "" },
      { ledger_id: "", dr: 0, cr: 0, narration: "" },
    ],
    active: true,
  };
}

function RecurringModal({ open, initial, onClose, onSaved }) {
  const [f, setF] = useState(emptyForm());
  const [ledgers, setLedgers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/ledgers").then((r) => setLedgers(r.data.ledgers || [])).catch(() => {});
    setF(initial ? {
      name: initial.name, voucher_type: initial.voucher_type,
      frequency: initial.frequency,
      start_date: initial.start_date, end_date: initial.end_date || "",
      narration: initial.narration || "", party_id: initial.party_id || "",
      lines: initial.lines?.length ? initial.lines : emptyForm().lines,
      active: initial.active !== false,
    } : emptyForm());
  }, [open, initial]);

  const totals = useMemo(() => {
    const dr = f.lines.reduce((s, l) => s + Number(l.dr || 0), 0);
    const cr = f.lines.reduce((s, l) => s + Number(l.cr || 0), 0);
    return { dr: Math.round(dr * 100) / 100, cr: Math.round(cr * 100) / 100 };
  }, [f.lines]);

  if (!open) return null;

  const updLine = (i, patch) => setF((s) => ({
    ...s,
    lines: s.lines.map((l, x) => x === i ? { ...l, ...patch } : l),
  }));
  const addLine = () => setF((s) => ({
    ...s,
    lines: [...s.lines, { ledger_id: "", dr: 0, cr: 0, narration: "" }],
  }));
  const rmLine = (i) => setF((s) => ({
    ...s,
    lines: s.lines.filter((_, x) => x !== i),
  }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    if (Math.abs(totals.dr - totals.cr) > 0.01) {
      toast.error(`Debit (${totals.dr}) ≠ Credit (${totals.cr})`);
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...f,
        lines: f.lines
          .filter((l) => l.ledger_id && (Number(l.dr) > 0 || Number(l.cr) > 0))
          .map((l) => ({
            ledger_id: l.ledger_id,
            dr: Number(l.dr || 0), cr: Number(l.cr || 0),
            narration: l.narration || "",
          })),
      };
      if (initial) await api.put(`/recurring-vouchers/${initial.id}`, body);
      else await api.post("/recurring-vouchers", body);
      toast.success(initial ? "Updated" : "Recurring voucher created");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="rv-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Recurring Voucher</div>
            <h2 className="text-xl font-bold text-[#0F172A]">{initial ? `Edit ${initial.name}` : "New Recurring Voucher"}</h2>
          </div>
          <button onClick={onClose} data-testid="rv-close"><X size={18}/></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><label className="label-field">Name *</label>
            <input className="input-clean" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Monthly Rent" data-testid="rv-name"/></div>
          <div><label className="label-field">Voucher Type</label>
            <select className="input-clean" value={f.voucher_type} onChange={(e) => setF({ ...f, voucher_type: e.target.value })} data-testid="rv-vtype">
              {VOUCHER_TYPES.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select></div>
          <div><label className="label-field">Frequency</label>
            <select className="input-clean" value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} data-testid="rv-frequency">
              {FREQUENCIES.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select></div>
          <div><label className="label-field">Start Date *</label>
            <input type="date" className="input-clean" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="rv-start"/></div>
          <div><label className="label-field">End Date</label>
            <input type="date" className="input-clean" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="rv-end"/></div>
          <div className="md:col-span-2"><label className="label-field">Narration</label>
            <input className="input-clean" value={f.narration} onChange={(e) => setF({ ...f, narration: e.target.value })}/></div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="overline">Lines (Dr / Cr must match)</div>
            <button className="btn-ghost text-xs" onClick={addLine} data-testid="rv-add-line">
              <Plus size={12}/> Add Line
            </button>
          </div>
          {f.lines.map((ln, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end mb-2">
              <div className="col-span-6">
                <select className="input-clean text-sm" value={ln.ledger_id}
                  onChange={(e) => updLine(i, { ledger_id: e.target.value })}
                  data-testid={`rv-ledger-${i}`}>
                  <option value="">Select ledger…</option>
                  {ledgers.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.group_name})</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <input type="number" step="0.01" className="input-clean text-sm text-right"
                  placeholder="Dr" value={ln.dr}
                  onChange={(e) => updLine(i, { dr: e.target.value, cr: 0 })}
                  data-testid={`rv-dr-${i}`}/>
              </div>
              <div className="col-span-2">
                <input type="number" step="0.01" className="input-clean text-sm text-right"
                  placeholder="Cr" value={ln.cr}
                  onChange={(e) => updLine(i, { cr: e.target.value, dr: 0 })}
                  data-testid={`rv-cr-${i}`}/>
              </div>
              <div className="col-span-1">
                <input className="input-clean text-sm" placeholder="Note"
                  value={ln.narration} onChange={(e) => updLine(i, { narration: e.target.value })}/>
              </div>
              <div className="col-span-1">
                <button className="text-[#DC2626]" onClick={() => rmLine(i)} data-testid={`rv-rm-${i}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
          <div className="mt-2 flex justify-end gap-6 text-sm font-mono-data">
            <span>Debit: <span className="font-bold" data-testid="rv-total-dr">{totals.dr.toFixed(2)}</span></span>
            <span>Credit: <span className="font-bold" data-testid="rv-total-cr">{totals.cr.toFixed(2)}</span></span>
            <span className={`font-bold ${Math.abs(totals.dr - totals.cr) < 0.01 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
              Diff: {(totals.dr - totals.cr).toFixed(2)}
            </span>
          </div>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm text-[#0F172A]">
          <input type="checkbox" className="w-4 h-4 accent-[#FF5500]" checked={f.active}
            onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="rv-active"/>
          Active — auto-generate drafts on schedule
        </label>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button className="btn-primary w-full sm:w-auto justify-center" onClick={save} disabled={saving} data-testid="rv-save">
            {saving ? "Saving…" : (initial ? "Save Changes" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RecurringVouchers() {
  const { active } = useCompany();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.get("/recurring-vouchers");
      setRows(data.recurring_vouchers || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);

  const runNow = async (r) => {
    if (!window.confirm(`Generate a draft voucher now from "${r.name}"?`)) return;
    try {
      await api.post(`/recurring-vouchers/${r.id}/run-now`);
      toast.success("Draft voucher generated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (r) => {
    try {
      await api.put(`/recurring-vouchers/${r.id}`, { ...r, active: !r.active });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete recurring voucher "${r.name}"?`)) return;
    try {
      await api.delete(`/recurring-vouchers/${r.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cancelAndRefund = async (r) => {
    const ok = window.confirm(
      `Cancel "${r.name}" and issue a prorated credit note for the ` +
      `unused portion of the current period?\n\n` +
      `• Template is deactivated immediately.\n` +
      `• Latest paid invoice from this template is refunded pro-rata.\n` +
      `• Reversing journal + credit note PDF emailed to the customer.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/recurring-vouchers/${r.id}/cancel-and-refund`);
      if (data.refunded === 0) {
        toast.info(data.reason ? `Cancelled — ${data.reason.replace(/_/g, " ")}` : "Cancelled");
      } else {
        toast.success(
          `Refunded ${data.amount} ${data.currency} · ${data.credit_note_number}`
        );
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cur = active?.base_currency || "INR";

  return (
    <div className="space-y-4" data-testid="recurring-vouchers-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Automation</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Repeat className="text-[#FF5500]" size={24}/> Recurring Vouchers
          </h1>
          <div className="text-sm text-[#64748B] mt-1">
            Schedule vouchers on a weekly / monthly / quarterly / yearly cadence.
            Each occurrence is created as a <b>draft</b> for you to review before posting.
          </div>
        </div>
        <button className="btn-primary w-full sm:w-auto justify-center shrink-0" onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="new-rv-btn">
          <Plus size={14}/> New Recurring Voucher
        </button>
      </div>

      <div className="card-surface p-4">
        {rows.length === 0 ? (
          <div className="text-center py-12">
            <CalendarClock size={48} className="mx-auto text-[#CBD5E1] mb-3"/>
            <p className="text-[#64748B] text-sm">
              {loading ? "Loading…" : "No recurring vouchers yet."}
            </p>
          </div>
        ) : (
          <table className="table-clean">
            <thead>
              <tr>
                <th>Name</th><th>Type</th><th>Frequency</th>
                <th>Next Run</th><th>Last Run</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Generated</th>
                <th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const total = (r.lines || []).reduce((s, l) => s + Number(l.dr || 0), 0);
                return (
                  <tr key={r.id} data-testid={`rv-row-${r.id}`}>
                    <td className="font-medium text-[#0F172A]">{r.name}</td>
                    <td><span className="chip chip-info text-[10px] uppercase">{r.voucher_type}</span></td>
                    <td className="capitalize">{r.frequency}</td>
                    <td className="font-mono-data text-xs">{r.next_run_date || "-"}</td>
                    <td className="font-mono-data text-xs text-[#64748B]">{r.last_run_date || "—"}</td>
                    <td className="text-right font-mono-data font-semibold">{fmtMoney(total, cur)}</td>
                    <td className="text-right font-mono-data">{r.generated_count}</td>
                    <td>
                      {r.active
                        ? <span className="chip chip-success">Active</span>
                        : <span className="chip chip-neutral">Paused</span>}
                    </td>
                    <td>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => runNow(r)} title="Generate now" className="text-[#16A34A]" data-testid={`rv-run-${r.id}`}>
                          <Play size={14}/>
                        </button>
                        <button onClick={() => toggleActive(r)} title={r.active ? "Pause" : "Resume"} className="text-[#F59E0B]">
                          <PauseCircle size={14}/>
                        </button>
                        <button
                          onClick={() => cancelAndRefund(r)}
                          title="Cancel & issue prorated refund"
                          className="text-[#B45309] hover:text-[#92400E]"
                          data-testid={`rv-cancel-refund-${r.id}`}
                        >
                          <RotateCcw size={14}/>
                        </button>
                        <button onClick={() => { setEditing(r); setModalOpen(true); }} title="Edit" className="text-[#64748B] hover:text-[#0F172A]" data-testid={`rv-edit-${r.id}`}>
                          <Edit3 size={14}/>
                        </button>
                        <button onClick={() => del(r)} title="Delete" className="text-[#DC2626]" data-testid={`rv-del-${r.id}`}>
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <RecurringModal open={modalOpen} initial={editing}
        onClose={() => setModalOpen(false)} onSaved={load}/>
    </div>
  );
}
